import api from '@api';
import { returnReasons } from 'skladon-types';
import { OptionType } from 'src/components/Select';
import { OrderType } from 'src/types/order';
import { ReturnProductType, ReturnType } from 'src/types/return';
import { CREATE_RETURN_STATUSES } from '../AllOrdersContainer/constants';
import { ORDER_COLUMNS_LOAD, statusesTo } from './constants';
import { ConstantsCodeType, ConstantsCodesObjectType } from 'src/types/constantsCodes';
import { checkPermission } from 'src/utils/checkPermission';
import { getOrderProductDisplayName, getOrderProductDisplaySku } from 'src/utils/orderProductDisplay';

export const getDefaultReturnReason = (skladonUser?: number): string | undefined =>
    skladonUser ? returnReasons.unknown : undefined;

export const getAllowedStatuses = (
    orderStatusCodes: ConstantsCodeType[],
    orderStatusCodesObject: ConstantsCodesObjectType,
    orderData?: { STATUS?: string } | null
): ConstantsCodeType[] => {
    if (checkPermission('RETURN.EDITALL')) {
        return orderStatusCodes;
    }

    if (!orderData || !orderData.STATUS) {
        return [];
    }

    if (!checkPermission(`RETURN.STATUS.EDIT.${orderData.STATUS.toUpperCase()}`)) {
        return [];
    }

    return [
        ...(statusesTo[orderData && orderData.STATUS] || [])
            .filter((statusKey) => {
                return checkPermission(`RETURN.STATUS.LIST.${statusKey.toUpperCase()}`);
            })
            .map((statusKey) => {
                return orderStatusCodesObject[statusKey];
            }),
    ];
};

// load orders for create return
export const loadOrdersData = async (setOrdersOptions: React.Dispatch<React.SetStateAction<OptionType[]>>) => {
    const res = await api('post', `api/secure/orders/grid`, {
        columns: ORDER_COLUMNS_LOAD,
        filters: [
            [
                {
                    column: 'STATUS',
                    operator: 'in',
                    value: CREATE_RETURN_STATUSES,
                },
            ],
        ],
        sorts: [{ column: 'ID', direction: 'DESC' }],
        items: 1000,
        page: 1,
    });
    const orderResData = res.data.data.data.map(
        (order: OrderType): OptionType => ({
            value: order.ID,
            label: `${order.ID} | ${order.ESHOP_ORDER_ID} | ${order.TRACKING_NUMBER} | ${order.ORDER_TYPE}`,
        })
    );
    setOrdersOptions(orderResData);
};

// load order detail
export const loadOrderData = async (
    idOrder: number,
    setOrderData: React.Dispatch<React.SetStateAction<OptionType[]>>,
    setLoadingOrderData: React.Dispatch<React.SetStateAction<boolean>>
) => {
    setLoadingOrderData(true);
    const res = await api('get', `api/secure/orders/${idOrder}`);
    setLoadingOrderData(false);
    setOrderData(res.data.data);
};

export const getDataFromOrder = (orderData: OrderType, editData: ReturnType, skladonUser?: number) => {
    const newProducts: ReturnProductType[] = [];
    const defaultReturnReason = getDefaultReturnReason(skladonUser);

    orderData.ORDER_PRODUCTS?.filter((orderProduct) => orderProduct.LINE_STATE !== 'D').forEach(
        (orderProduct, index) => {
            const settedProduct = newProducts.find((product) => product.PRODUCT_ID === orderProduct.PRODUCT_ID);
            if (settedProduct) {
                settedProduct.COUNT = Number(settedProduct.COUNT || 0) + Number(orderProduct.COUNT || 0);
            } else {
                newProducts.push({
                    ID: -(index + 1),
                    PRODUCT_ID: orderProduct.PRODUCT_ID,
                    NAME: getOrderProductDisplayName(orderProduct),
                    SKU: getOrderProductDisplaySku(orderProduct),
                    QUANTITY: Number(orderProduct.COUNT),
                    COUNT: Number(orderProduct.COUNT),
                    LINE_STATE: 'E',
                    ...(defaultReturnReason ? { RETURN_REASON: defaultReturnReason } : {}),
                });
            }
        }
    );

    return {
        ...editData,
        ORDER_ID: orderData.ID,
        ORDER_TYPE: orderData.ORDER_TYPE,
        RETURNED_ORDER_PRODUCTS: newProducts,
    };
};
